/**
 * AuditDashboardScreen.js
 * JobEng — Company-wide document audit dashboard
 *
 * Full-screen route showing:
 *   - 4 hero stat cards (total events, unique users, top action, top doc)
 *   - Action breakdown (horizontal bars)
 *   - Top 10 most-active users
 *   - Top 10 most-accessed documents
 *   - Recent 20 events
 *
 * Path:
 *   app/screens/business/documents/AuditDashboardScreen.js
 *
 * Navigation:
 *   navigation.navigate('AuditDashboard')
 *
 * Backend:
 *   GET /api/docs/audit/summary?days=30
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator,
  RefreshControl, Dimensions, Platform,
} from 'react-native';
import { SafeAreaView }           from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Ionicons }               from '@expo/vector-icons';
import { LinearGradient }         from 'expo-linear-gradient';
import AsyncStorage               from '@react-native-async-storage/async-storage';
import axios                      from 'axios';

const { width } = Dimensions.get('window');
const IS_PROMAX = width >= 430;
const HPAD    = IS_PROMAX ? 18 : 14;
const CARD_R  = IS_PROMAX ? 16 : 12;
const FS_XS   = IS_PROMAX ? 11 : 10;
const FS_SM   = IS_PROMAX ? 13 : 11;
const FS_BASE = IS_PROMAX ? 15 : 13;
const FS_MD   = IS_PROMAX ? 16 : 14;
const FS_LG   = IS_PROMAX ? 18 : 16;
const FS_XL   = IS_PROMAX ? 22 : 19;
const FS_XXL  = IS_PROMAX ? 28 : 24;

const C = {
  primary:'#0e4e96', accent:'#19abb5', gold:'#fdb913',
  success:'#10b981', warning:'#f59e0b', danger:'#dc2626', purple:'#8b5cf6',
  bg:'#f1f5f9', card:'#fff', border:'#e2e8f0',
  dark:'#0f172a', mid:'#475569', light:'#94a3b8',
};

const API_BASE = 'https://api.jobeng.ai';
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
    'Content-Type': 'application/json',
  },
  timeout: 12000,
});
api.interceptors.request.use(async (config) => {
  try {
    const token = (await AsyncStorage.getItem('auth_token'))
              || (await AsyncStorage.getItem('userToken'))
              || (await AsyncStorage.getItem('token'));
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    const bizId = await AsyncStorage.getItem('company_id');
    if (bizId) {
      config.params = config.params || {};
      config.params.company_id = parseInt(bizId);
    }
  } catch {}
  return config;
});

// Action label/color mapping (matches AuditTrailModal)
const ACTION_LABELS = {
  view:'Viewed', preview:'Previewed', download:'Downloaded', upload:'Uploaded',
  edit:'Edited', delete:'Deleted', approve:'Approved', reject:'Rejected',
  archive:'Archived', restore:'Restored', pin:'Pinned', unpin:'Unpinned',
  favourite:'Favourited', unfavourite:'Unfavourited', lock:'Locked', unlock:'Unlocked',
  share:'Shared', duplicate:'Duplicated', version_add:'New version', version_restore:'Restored version',
  comment_add:'Commented', comment_resolve:'Resolved comment', comment_delete:'Deleted comment',
  reminder_set:'Reminder set', reminder_delete:'Reminder deleted',
  tag_add:'Tagged', tag_remove:'Untagged', acknowledge:'Acknowledged', sign:'Signed',
  link_add:'Linked', link_remove:'Unlinked',
};
const ACTION_COLORS = {
  view:C.light, preview:C.light, download:C.accent, upload:C.primary,
  edit:C.warning, delete:C.danger, approve:C.success, reject:C.danger,
  archive:C.mid, restore:C.success, share:C.accent, duplicate:C.purple,
  acknowledge:C.success, sign:C.primary,
};
const colorFor = (a) => ACTION_COLORS[a] || C.mid;
const labelFor = (a) => ACTION_LABELS[a] || a;

const DAY_WINDOWS = [
  { id:7,   label:'7 days'  },
  { id:30,  label:'30 days' },
  { id:90,  label:'90 days' },
  { id:365, label:'1 year'  },
];

const timeAgo = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60)      return 'just now';
  if (diff < 3600)    return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400)   return `${Math.floor(diff/3600)}h ago`;
  if (diff < 7*86400) return `${Math.floor(diff/86400)}d ago`;
  try { return d.toLocaleDateString(); } catch { return iso; }
};

export default function AuditDashboardScreen({ navigation }) {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState(null);
  const [days,       setDays]       = useState(30);
  const [companyId,  setCompanyId]  = useState(null);

  // Load company_id once
  useEffect(() => {
    (async () => {
      try {
        const id = await AsyncStorage.getItem('company_id');
        setCompanyId(id ? parseInt(id) : null);
      } catch {}
    })();
  }, []);

  const fetchSummary = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get('/api/docs/audit/summary', { params: { days } });
      if (!res.data?.success) throw new Error(res.data?.error || 'Fetch failed');
      setData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [days]);

  useEffect(() => { setLoading(true); fetchSummary(); }, [days]);

  const onRefresh = useCallback(() => { setRefreshing(true); fetchSummary(); }, [fetchSummary]);

  // Derived stats for hero cards
  const stats = useMemo(() => {
    if (!data) return null;
    const totalEvents = (data.by_action || []).reduce((s, r) => s + (r.count || 0), 0);
    const uniqueUsers = new Set((data.top_users || []).map(u => u.user_id)).size;
    const topAction   = (data.by_action || [])[0];
    const topDoc      = (data.top_docs   || [])[0];
    return { totalEvents, uniqueUsers, topAction, topDoc };
  }, [data]);

  const maxActionCount = useMemo(() => {
    if (!data?.by_action?.length) return 1;
    return Math.max(...data.by_action.map(r => r.count));
  }, [data]);

  return (
    <SafeAreaView style={s.root} edges={['top']}>
      {/* Header */}
      <LinearGradient colors={[C.primary, C.accent]} style={s.header}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={s.headerBtn} hitSlop={{ top:10, bottom:10, left:10, right:10 }}>
          <Ionicons name="chevron-back" size={26} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex:1 }}>
          <Text style={s.headerTitle}>Audit Dashboard</Text>
          <Text style={s.headerSub}>Document activity across your company</Text>
        </View>
        <TouchableOpacity onPress={onRefresh} style={s.headerBtn} hitSlop={{ top:10, bottom:10, left:10, right:10 }}>
          <MaterialCommunityIcons name="refresh" size={22} color="#fff" />
        </TouchableOpacity>
      </LinearGradient>

      {/* Day window selector */}
      <View style={s.windowBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal:HPAD, gap:8 }}>
          {DAY_WINDOWS.map(w => {
            const active = days === w.id;
            return (
              <TouchableOpacity
                key={w.id}
                style={[s.windowChip, active && s.windowChipActive]}
                onPress={() => setDays(w.id)}
                activeOpacity={0.7}
              >
                <Text style={[s.windowChipTxt, active && s.windowChipTxtActive]}>{w.label}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>

      {/* Body */}
      {error ? (
        <View style={s.errBox}>
          <MaterialCommunityIcons name="alert-circle-outline" size={42} color={C.danger} />
          <Text style={s.errTitle}>Couldn't load dashboard</Text>
          <Text style={s.errMsg}>{error}</Text>
          <TouchableOpacity style={s.retryBtn} onPress={fetchSummary} activeOpacity={0.85}>
            <Text style={s.retryBtnTxt}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : loading ? (
        <View style={s.loadBox}>
          <ActivityIndicator size="large" color={C.primary} />
          <Text style={s.loadTxt}>Loading audit data…</Text>
        </View>
      ) : (
        <ScrollView
          contentContainerStyle={{ paddingBottom:40 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={C.primary} />}
        >
          {/* Hero stat cards */}
          <View style={s.heroGrid}>
            <StatCard
              icon="chart-timeline-variant"
              color={C.primary}
              value={stats?.totalEvents ?? 0}
              label="Total Events"
            />
            <StatCard
              icon="account-group"
              color={C.accent}
              value={stats?.uniqueUsers ?? 0}
              label="Active Users"
            />
            <StatCard
              icon="trending-up"
              color={C.success}
              value={stats?.topAction?.count ?? 0}
              label={stats?.topAction ? labelFor(stats.topAction.action) : 'Top Action'}
              small
            />
            <StatCard
              icon="file-document-outline"
              color={C.gold}
              value={stats?.topDoc?.unique_users ?? 0}
              label={stats?.topDoc ? `Doc #${stats.topDoc.document_id}` : 'Top Document'}
              small
              hint="unique users"
            />
          </View>

          {/* Action breakdown */}
          <Section
            icon="poll"
            title="Activity by Action"
            subtitle={`${data?.by_action?.length || 0} action types in last ${days} days`}
          >
            {(data?.by_action || []).length === 0 ? (
              <EmptyState message="No activity in this window" />
            ) : (
              <View style={{ gap:10 }}>
                {(data.by_action || []).map(row => (
                  <View key={row.action} style={s.barRow}>
                    <View style={s.barHeader}>
                      <View style={[s.barDot, { backgroundColor: colorFor(row.action) }]} />
                      <Text style={s.barLabel}>{labelFor(row.action)}</Text>
                      <Text style={s.barCount}>{row.count}</Text>
                    </View>
                    <View style={s.barTrack}>
                      <View
                        style={[s.barFill, {
                          width: `${Math.max(2, (row.count / maxActionCount) * 100)}%`,
                          backgroundColor: colorFor(row.action),
                        }]}
                      />
                    </View>
                  </View>
                ))}
              </View>
            )}
          </Section>

          {/* Top users */}
          <Section
            icon="account-star"
            title="Most Active Users"
            subtitle="Sorted by event count"
          >
            {(data?.top_users || []).length === 0 ? (
              <EmptyState message="No active users yet" />
            ) : (
              <View>
                {(data.top_users || []).map((u, idx) => (
                  <View key={u.user_id || idx} style={s.rankRow}>
                    <View style={[s.rankBadge, { backgroundColor: idx < 3 ? `${C.gold}22` : `${C.light}18` }]}>
                      <Text style={[s.rankBadgeTxt, { color: idx < 3 ? C.gold : C.mid }]}>{idx + 1}</Text>
                    </View>
                    <View style={{ flex:1 }}>
                      <Text style={s.rankName} numberOfLines={1}>{u.user_name || `User ${u.user_id}`}</Text>
                      <Text style={s.rankSub}>ID: {u.user_id}</Text>
                    </View>
                    <View style={s.rankRight}>
                      <Text style={s.rankCount}>{u.event_count}</Text>
                      <Text style={s.rankCountLbl}>events</Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </Section>

          {/* Top documents */}
          <Section
            icon="file-multiple"
            title="Most Accessed Documents"
            subtitle="By number of audit events"
          >
            {(data?.top_docs || []).length === 0 ? (
              <EmptyState message="No document activity yet" />
            ) : (
              <View>
                {(data.top_docs || []).map((d, idx) => (
                  <View key={d.document_id || idx} style={s.rankRow}>
                    <View style={[s.rankBadge, { backgroundColor: `${C.accent}22` }]}>
                      <MaterialCommunityIcons name="file-document-outline" size={18} color={C.accent} />
                    </View>
                    <View style={{ flex:1 }}>
                      <Text style={s.rankName}>Document #{d.document_id}</Text>
                      <Text style={s.rankSub}>{d.unique_users} unique user{d.unique_users === 1 ? '' : 's'}</Text>
                    </View>
                    <View style={s.rankRight}>
                      <Text style={s.rankCount}>{d.event_count}</Text>
                      <Text style={s.rankCountLbl}>events</Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </Section>

          {/* Recent activity */}
          <Section
            icon="history"
            title="Recent Activity"
            subtitle="Last 20 events across all documents"
          >
            {(data?.recent || []).length === 0 ? (
              <EmptyState message="No recent activity" />
            ) : (
              <View>
                {(data.recent || []).map((ev) => (
                  <View key={ev.id} style={s.recentRow}>
                    <View style={[s.recentDot, { backgroundColor: colorFor(ev.action) }]} />
                    <View style={{ flex:1 }}>
                      <Text style={s.recentName} numberOfLines={1}>
                        <Text style={{ fontWeight:'700', color:C.dark }}>{ev.user_name || `User ${ev.user_id}`}</Text>
                        <Text style={{ color:C.mid }}>  {labelFor(ev.action).toLowerCase()}</Text>
                        <Text style={{ color:C.light }}>  · doc #{ev.document_id}</Text>
                      </Text>
                      <Text style={s.recentTime}>{timeAgo(ev.created_at)}</Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </Section>

          <Text style={s.footnote}>
            Window: last {data?.days_window || days} days · Company ID: {companyId || '—'}
          </Text>
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

const StatCard = ({ icon, color, value, label, small, hint }) => (
  <View style={[s.statCard, small && s.statCardSmall]}>
    <View style={[s.statIcon, { backgroundColor: `${color}18` }]}>
      <MaterialCommunityIcons name={icon} size={small ? 18 : 22} color={color} />
    </View>
    <Text style={[s.statValue, small && s.statValueSmall]} numberOfLines={1}>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </Text>
    <Text style={s.statLabel} numberOfLines={1}>{label}</Text>
    {hint && <Text style={s.statHint}>{hint}</Text>}
  </View>
);

const Section = ({ icon, title, subtitle, children }) => (
  <View style={s.section}>
    <View style={s.sectionHeader}>
      <MaterialCommunityIcons name={icon} size={18} color={C.primary} />
      <View style={{ flex:1, marginLeft:8 }}>
        <Text style={s.sectionTitle}>{title}</Text>
        {!!subtitle && <Text style={s.sectionSub}>{subtitle}</Text>}
      </View>
    </View>
    <View style={s.sectionBody}>{children}</View>
  </View>
);

const EmptyState = ({ message }) => (
  <View style={s.emptyMini}>
    <MaterialCommunityIcons name="inbox-outline" size={28} color={C.light} />
    <Text style={s.emptyMiniTxt}>{message}</Text>
  </View>
);

// ── Styles ──────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root:        { flex:1, backgroundColor:C.bg },
  header:      { flexDirection:'row', alignItems:'center', paddingHorizontal:HPAD, paddingTop:Platform.OS === 'android' ? 16 : 8, paddingBottom:14, gap:6 },
  headerBtn:   { width:36, height:36, justifyContent:'center', alignItems:'center' },
  headerTitle: { fontSize:FS_LG, fontWeight:'700', color:'#fff', letterSpacing:-0.2 },
  headerSub:   { fontSize:FS_SM, color:'rgba(255,255,255,0.85)', marginTop:2 },

  windowBar:   { backgroundColor:C.card, paddingVertical:12, borderBottomWidth:1, borderBottomColor:C.border },
  windowChip:  { paddingHorizontal:14, paddingVertical:8, borderRadius:22, backgroundColor:'#f1f5f9', borderWidth:1, borderColor:C.border },
  windowChipActive: { backgroundColor:`${C.primary}18`, borderColor:C.primary },
  windowChipTxt: { fontSize:FS_SM, fontWeight:'600', color:C.mid },
  windowChipTxtActive: { color:C.primary, fontWeight:'700' },

  heroGrid:    { flexDirection:'row', flexWrap:'wrap', padding:HPAD, gap:10 },
  statCard:    { width: (width - HPAD * 2 - 10) / 2, backgroundColor:C.card, padding:IS_PROMAX?16:13, borderRadius:CARD_R, borderWidth:1, borderColor:C.border },
  statCardSmall: {},
  statIcon:    { width:36, height:36, borderRadius:18, justifyContent:'center', alignItems:'center', marginBottom:10 },
  statValue:   { fontSize:FS_XXL, fontWeight:'800', color:C.dark, letterSpacing:-0.5 },
  statValueSmall: { fontSize:FS_XL },
  statLabel:   { fontSize:FS_SM, color:C.mid, fontWeight:'600', marginTop:4 },
  statHint:    { fontSize:FS_XS, color:C.light, marginTop:2 },

  section:     { backgroundColor:C.card, marginHorizontal:HPAD, marginBottom:14, borderRadius:CARD_R, borderWidth:1, borderColor:C.border, overflow:'hidden' },
  sectionHeader: { flexDirection:'row', alignItems:'center', padding:IS_PROMAX?14:12, borderBottomWidth:1, borderBottomColor:'#f8fafc' },
  sectionTitle: { fontSize:FS_MD, fontWeight:'700', color:C.dark },
  sectionSub:  { fontSize:FS_XS, color:C.light, marginTop:2 },
  sectionBody: { padding:IS_PROMAX?14:12 },

  barRow:      {},
  barHeader:   { flexDirection:'row', alignItems:'center', marginBottom:6, gap:8 },
  barDot:      { width:8, height:8, borderRadius:4 },
  barLabel:    { flex:1, fontSize:FS_SM, color:C.dark, fontWeight:'600' },
  barCount:    { fontSize:FS_SM, color:C.mid, fontWeight:'700' },
  barTrack:    { height:6, backgroundColor:'#f1f5f9', borderRadius:3, overflow:'hidden' },
  barFill:     { height:'100%', borderRadius:3 },

  rankRow:     { flexDirection:'row', alignItems:'center', paddingVertical:10, gap:12, borderBottomWidth:1, borderBottomColor:'#f8fafc' },
  rankBadge:   { width:32, height:32, borderRadius:16, justifyContent:'center', alignItems:'center' },
  rankBadgeTxt:{ fontSize:FS_SM, fontWeight:'800' },
  rankName:    { fontSize:FS_BASE, color:C.dark, fontWeight:'600' },
  rankSub:     { fontSize:FS_XS, color:C.light, marginTop:2 },
  rankRight:   { alignItems:'flex-end' },
  rankCount:   { fontSize:FS_MD, fontWeight:'800', color:C.primary },
  rankCountLbl:{ fontSize:FS_XS, color:C.light },

  recentRow:   { flexDirection:'row', alignItems:'flex-start', paddingVertical:10, gap:10, borderBottomWidth:1, borderBottomColor:'#f8fafc' },
  recentDot:   { width:8, height:8, borderRadius:4, marginTop:7 },
  recentName:  { fontSize:FS_BASE, color:C.dark, lineHeight:IS_PROMAX?20:18 },
  recentTime:  { fontSize:FS_XS, color:C.light, marginTop:3 },

  emptyMini:   { alignItems:'center', padding:20, gap:6 },
  emptyMiniTxt:{ fontSize:FS_SM, color:C.light },

  footnote:    { textAlign:'center', fontSize:FS_XS, color:C.light, paddingHorizontal:HPAD, paddingTop:10 },

  loadBox:     { flex:1, justifyContent:'center', alignItems:'center', gap:10, padding:24 },
  loadTxt:     { fontSize:FS_BASE, color:C.mid },

  errBox:      { flex:1, justifyContent:'center', alignItems:'center', gap:8, paddingHorizontal:30 },
  errTitle:    { fontSize:FS_LG, fontWeight:'700', color:C.danger, marginTop:6 },
  errMsg:      { fontSize:FS_BASE, color:C.mid, textAlign:'center', marginBottom:8 },
  retryBtn:    { paddingHorizontal:24, paddingVertical:12, borderRadius:CARD_R, backgroundColor:C.primary },
  retryBtnTxt: { color:'#fff', fontSize:FS_MD, fontWeight:'700' },
});
